import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useRecordContext,
} from "ra-core";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  ChevronDown,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import ReactMarkdown from "react-markdown";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { streamSkillRun } from "@/lib/agenticClient";
import { BriefAgentSheet } from "../agentic/BriefAgentSheet";

import type { Contact } from "../types";

// Sprint 7 T7.1.c — Refonte visuelle conforme à design/atomic-crm/
// brief.jsx (composant BriefIA). Aligné avec BriefDailyBanner (T7.1.b)
// pour cohérence : fond pastel violet, label uppercase, markdown rendu
// en HTML stylé via react-markdown.
//
// ponytail: un seul composant `BriefBanner` paramétré ; la variante company
// vit dans companies/CompanyBriefIABanner.tsx (thème vert + action bar).

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;
const SKILL_MODEL_USED = "deepseek-chat";

export interface ContactBrief {
  id: string;
  contact_id: number;
  content: string;
  generated_at: string;
  generated_by: string | null;
  model_used: string | null;
  is_current: boolean;
  created_at: string;
}

type BriefRow = Omit<ContactBrief, "contact_id">;

type BriefState = "empty" | "generated" | "stale" | "pending" | "error";

function deriveState(
  brief: BriefRow | undefined,
  pending: boolean,
  errorMessage: string | null,
): BriefState {
  if (pending) return "pending";
  if (errorMessage) return "error";
  if (!brief) return "empty";
  const age = Date.now() - new Date(brief.generated_at).getTime();
  return age >= STALE_THRESHOLD_MS ? "stale" : "generated";
}

function readCollapsed(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Classes Tailwind qui diffèrent entre le thème contact (violet) et company (vert). */
export type BriefTheme = {
  container: string;
  containerError: string;
  icon: string;
  iconError: string;
  label: string;
  labelError: string;
  staleBadge: string;
  muted: string;
  collapseHover: string;
  skeleton: string;
  marker: string;
  link: string;
  code?: string;
  em?: string;
};

const CONTACT_BRIEF_THEME: BriefTheme = {
  container:
    "bg-gradient-to-b from-violet-50 to-violet-100/50 border-violet-200 dark:from-violet-950/30 dark:to-violet-900/20 dark:border-violet-900/40",
  containerError:
    "bg-gradient-to-b from-red-50 to-red-100/60 border-red-200 dark:from-red-950/30 dark:to-red-900/20 dark:border-red-900/40",
  icon: "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300",
  iconError: "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300",
  label: "text-violet-800 dark:text-violet-300",
  labelError: "text-red-800 dark:text-red-300",
  staleBadge:
    "bg-amber-100/80 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300",
  muted: "text-muted-foreground",
  collapseHover: "hover:bg-violet-100/60 dark:hover:bg-violet-900/30",
  skeleton: "bg-violet-200/60 dark:bg-violet-800/40",
  marker: "marker:text-violet-500/70",
  link: "text-violet-700 hover:underline dark:text-violet-300",
  code: "rounded bg-violet-100/70 dark:bg-violet-900/40 px-1 py-0.5 text-[12px] font-mono",
};

type BriefBannerProps = {
  /** Table des briefs (contact_briefs / company_briefs). */
  resource: string;
  /** Colonne FK vers l'entité (contact_id / company_id). */
  foreignKey: string;
  skillId: string;
  entityType: "contact" | "company";
  entityLabel: string;
  /** Titre du label uppercase (ex: "Brief IA"). */
  title: string;
  testId: string;
  collapseStorageKey: string;
  emptyTitle: string;
  emptyDescription: string;
  /** Overrides du thème contact (violet). */
  theme?: Partial<BriefTheme>;
  /** Rendu sous le markdown (ex: action bar Appeler / Email). */
  extraActions?: ReactNode;
};

export function BriefBanner({
  resource,
  foreignKey,
  skillId,
  entityType,
  entityLabel,
  title,
  testId,
  collapseStorageKey,
  emptyTitle,
  emptyDescription,
  theme: themeOverrides,
  extraActions,
}: BriefBannerProps) {
  const theme: BriefTheme = { ...CONTACT_BRIEF_THEME, ...themeOverrides };
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readCollapsed(collapseStorageKey),
  );
  const [agentSheetOpen, setAgentSheetOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(collapseStorageKey, collapsed ? "1" : "0");
    } catch {
      // localStorage indisponible — fail silently
    }
  }, [collapsed, collapseStorageKey]);

  const enabled = !!record?.id;
  const query = useGetList<BriefRow>(
    resource,
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "generated_at", order: "DESC" },
      filter: { [foreignKey]: record?.id, is_current: true },
    },
    { enabled },
  );

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  if (!record) return null;

  const brief = query.data?.[0];
  const state = deriveState(brief, pending, errorMessage);

  const handleGenerate = async () => {
    if (!record.id) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setPending(true);
    setErrorMessage(null);
    let content: string | null = null;

    try {
      for await (const ev of streamSkillRun(
        skillId,
        { [foreignKey]: record.id },
        { signal: ctrl.signal },
      )) {
        if (ev.event === "run.done") {
          const out = (ev.data as { output?: { content?: string } }).output;
          content = out?.content ?? null;
        } else if (ev.event === "run.error") {
          throw new Error(
            (ev.data as { error?: string }).error ?? "skill_run_failed",
          );
        }
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = (err as Error).message;
      setErrorMessage(msg);
      notify(`Génération du brief échouée : ${msg}`, { type: "error" });
      setPending(false);
      return;
    }

    if (!content) {
      const msg = "Le brief généré est vide.";
      setErrorMessage(msg);
      notify(msg, { type: "warning" });
      setPending(false);
      return;
    }

    try {
      await dataProvider.create(resource, {
        data: {
          [foreignKey]: record.id,
          content,
          model_used: SKILL_MODEL_USED,
          is_current: true,
        },
      });
      await query.refetch();
    } catch (err) {
      notify(`Brief généré mais non sauvegardé : ${(err as Error).message}`, {
        type: "warning",
      });
    } finally {
      setPending(false);
    }
  };

  const isErrorVariant = state === "error";
  const showBrief = (state === "generated" || state === "stale") && brief;

  return (
    <div
      className={cn(
        "rounded-xl border px-4 py-3.5 md:py-4 flex gap-3.5 md:gap-4 items-start",
        isErrorVariant ? theme.containerError : theme.container,
      )}
      data-testid={testId}
    >
      <div
        className={cn(
          "size-8 shrink-0 rounded-lg flex items-center justify-center",
          isErrorVariant ? theme.iconError : theme.icon,
        )}
      >
        {state === "error" ? (
          <AlertTriangle className="size-4" />
        ) : (
          <Sparkles className="size-4" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div
            className={cn(
              "text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1.5 mb-0 flex-1 min-w-0",
              isErrorVariant ? theme.labelError : theme.label,
            )}
          >
            <span>
              {state === "error"
                ? "Brief IA · erreur"
                : state === "stale"
                  ? "Brief IA · obsolète"
                  : title}
            </span>
            {state === "stale" && (
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium normal-case tracking-normal",
                  theme.staleBadge,
                )}
              >
                <AlertTriangle className="size-2.5" />
                {">"}24h
              </span>
            )}
            {showBrief && collapsed && (
              <span
                className={cn(
                  "ml-2 normal-case font-normal tracking-normal",
                  theme.muted,
                )}
              >
                · généré{" "}
                {formatDistanceToNowStrict(new Date(brief.generated_at), {
                  addSuffix: true,
                  locale: fr,
                })}
              </span>
            )}
          </div>
          {(state === "generated" || state === "stale") && (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? "Dérouler le brief" : "Réduire le brief"}
              aria-expanded={!collapsed}
              className={cn(
                "shrink-0 size-6 -mt-0.5 inline-flex items-center justify-center rounded hover:text-foreground",
                theme.muted,
                theme.collapseHover,
              )}
            >
              <ChevronDown
                className={cn(
                  "size-4 transition-transform",
                  collapsed && "-rotate-90",
                )}
              />
            </button>
          )}
        </div>

        {state === "pending" && (
          <div className="mt-1.5 flex flex-col gap-1.5">
            <div
              className={cn(
                "h-3 w-[94%] rounded animate-pulse",
                theme.skeleton,
              )}
            />
            <div
              className={cn(
                "h-3 w-[82%] rounded animate-pulse",
                theme.skeleton,
              )}
            />
            <div
              className={cn(
                "h-3 w-[60%] rounded animate-pulse",
                theme.skeleton,
              )}
            />
          </div>
        )}

        {state === "empty" && (
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-semibold text-foreground">
                {emptyTitle}
              </p>
              <p className={cn("text-[12.5px] mt-0.5", theme.muted)}>
                {emptyDescription}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              onClick={handleGenerate}
              disabled={pending}
              className="gap-1.5"
            >
              <Sparkles className="size-3.5" />
              Générer le brief IA
              <ArrowRight className="size-3.5" />
            </Button>
          </div>
        )}

        {state === "error" && (
          <div className="mt-1.5">
            <p className="text-[13px] text-foreground/90 leading-relaxed">
              Impossible de générer le brief : <strong>{errorMessage}</strong>
            </p>
            <div className="mt-2.5 flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="gap-1.5 h-7 px-2.5 text-xs"
                onClick={handleGenerate}
              >
                <RefreshCw className="size-3" />
                Réessayer
              </Button>
            </div>
          </div>
        )}

        {showBrief && !collapsed && (
          <div className="mt-1.5">
            <GeneratedBody
              brief={brief}
              theme={theme}
              onRegenerate={handleGenerate}
              onAskAgent={() => setAgentSheetOpen(true)}
              isStale={state === "stale"}
              extraActions={extraActions}
            />
          </div>
        )}
      </div>
      <BriefAgentSheet
        open={agentSheetOpen}
        onOpenChange={setAgentSheetOpen}
        entityType={entityType}
        entityId={record.id}
        entityLabel={entityLabel}
        briefContent={brief?.content}
      />
    </div>
  );
}

function GeneratedBody({
  brief,
  theme,
  onRegenerate,
  onAskAgent,
  isStale,
  extraActions,
}: {
  brief: BriefRow;
  theme: BriefTheme;
  onRegenerate: () => void;
  onAskAgent: () => void;
  isStale: boolean;
  extraActions?: ReactNode;
}) {
  const rel = formatDistanceToNowStrict(new Date(brief.generated_at), {
    addSuffix: true,
    locale: fr,
  });
  const sep = (
    <span aria-hidden className={cn("opacity-50", theme.muted)}>
      ·
    </span>
  );
  return (
    <>
      <div className="brief-markdown text-[13.5px] text-foreground/90 leading-relaxed max-w-[92ch]">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-2.5 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h2: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-2.5 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h3: ({ children }) => (
              <h4 className="text-[13px] font-semibold text-foreground mt-2 mb-0.5 first:mt-0">
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p className="my-1 first:mt-0 last:mb-0">{children}</p>
            ),
            ul: ({ children }) => (
              <ul
                className={cn("my-1 space-y-0.5 list-disc pl-5", theme.marker)}
              >
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="my-1 space-y-0.5 list-decimal pl-5">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-0.5">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">
                {children}
              </strong>
            ),
            a: ({ href, children }) => (
              <a href={href} className={theme.link}>
                {children}
              </a>
            ),
            ...(theme.code && {
              code: ({ children }) => (
                <code className={theme.code}>{children}</code>
              ),
            }),
            ...(theme.em && {
              em: ({ children }) => <em className={theme.em}>{children}</em>,
            }),
          }}
        >
          {brief.content}
        </ReactMarkdown>
      </div>
      {extraActions}
      <div
        className={cn(
          "mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]",
          theme.muted,
        )}
      >
        <span>Généré {rel}</span>
        {brief.model_used && (
          <>
            {sep}
            <span>{brief.model_used}</span>
          </>
        )}
        {sep}
        <Button
          type="button"
          size="sm"
          variant={isStale ? "default" : "ghost"}
          className={cn("gap-1.5 h-7 px-2 text-xs", isStale && "h-7 px-2.5")}
          onClick={onRegenerate}
        >
          <RefreshCw className="size-3" />
          Régénérer
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7 px-2 text-xs"
          onClick={onAskAgent}
        >
          <Bot className="size-3" />
          Demander à l'agent
        </Button>
      </div>
    </>
  );
}

export function BriefIABanner() {
  const record = useRecordContext<Contact>();
  if (!record) return null;
  const full =
    `${(record.first_name ?? "").trim()} ${(record.last_name ?? "").trim()}`.trim();
  return (
    <BriefBanner
      resource="contact_briefs"
      foreignKey="contact_id"
      skillId="brief_pre_call"
      entityType="contact"
      entityLabel={full || `Contact #${record.id}`}
      title="Brief IA"
      testId="brief-ia-banner"
      collapseStorageKey="brief-ia-banner-collapsed"
      emptyTitle="Aucun brief IA généré pour ce contact"
      emptyDescription="Le brief synthétise l'historique, les signaux produit et propose la meilleure prochaine action."
    />
  );
}
